import { useCallback, useMemo, useState } from 'react';
import { deleteField, doc, writeBatch } from 'firebase/firestore';

import { db } from '../../../firebase.js';
import {
    buildUserFilterGroups,
    countActiveUserFilters,
    DEFAULT_USER_SORT_COLUMN,
    selectVisibleUsers,
} from '../../../common/setupUserFilters.js';
import { describeFirestoreError } from '../../../common/firestoreErrors.js';
import {
    normalizeCompanyMembershipPermissionEntry,
    normalizeIdList,
    serializeCompanyMembershipPermissionEntry,
} from '../setupShared.js';
import useWelcomeEmailTemplates from './useWelcomeEmailTemplates.js';

// The user editor works on the fields firestore.rules lets an admin write on a user doc:
// companies, waste_receivers and company_memberships. isAdmin is deliberately not among
// them — promoting a user is not something this tab does.
const createEmptyUserDraft = () => ({
    companies: [],
    wasteReceivers: [],
    permissionsByCompanyId: {},
});

// Every piece of state the Brugere tab owns, plus the handlers that write it back. The
// page keeps the user list itself, since the other two tabs read it as well.
const useUsersTab = ({
    isActive,
    userOptions,
    wasteReceivers,
    companyOptions,
    wasteReceiverById,
    companyById,
    saving,
    setSaving,
    showMessage,
    loadingCompanies,
    loadingReceivers,
}) => {
    const [userFilterSelections, setUserFilterSelections] = useState({});
    const [userSearch, setUserSearch] = useState('');
    const [userSortColumn, setUserSortColumn] = useState(DEFAULT_USER_SORT_COLUMN);
    const [userSortDirection, setUserSortDirection] = useState('asc');
    const [editingUserId, setEditingUserId] = useState('');
    const [userDraft, setUserDraft] = useState(createEmptyUserDraft);
    // 'create' | 'templates' | '' — the provisioning dialogs, one open at a time.
    const [provisioningDialog, setProvisioningDialog] = useState('');
    const [welcomeEmailUserId, setWelcomeEmailUserId] = useState('');
    const welcomeEmailTemplates = useWelcomeEmailTemplates(isActive);
    const welcomeEmailUser = useMemo(
        () => userOptions.find((user) => user.id === welcomeEmailUserId) || null,
        [userOptions, welcomeEmailUserId],
    );
    const existingEmails = useMemo(
        () => new Set(userOptions.map((user) => String(user.email || '').trim().toLowerCase()).filter(Boolean)),
        [userOptions],
    );

    const userFilterGroups = useMemo(
        () => buildUserFilterGroups({ wasteReceivers, companies: companyOptions }),
        [wasteReceivers, companyOptions],
    );
    const getShortWasteReceiverLabel = useCallback(
        (receiverId) => wasteReceiverById.get(receiverId)?.name || receiverId,
        [wasteReceiverById],
    );
    const getShortCompanyLabel = useCallback(
        (companyId) => companyById.get(companyId)?.label || companyId,
        [companyById],
    );
    // Search matches the receivers and companies a user is attached to, and the attachment
    // columns sort by those names, so both need the ids resolved to display names.
    const userLabels = useMemo(
        () => ({
            getWasteReceiverLabel: getShortWasteReceiverLabel,
            getCompanyLabel: getShortCompanyLabel,
        }),
        [getShortWasteReceiverLabel, getShortCompanyLabel],
    );
    const visibleUsers = useMemo(
        () =>
            selectVisibleUsers(userOptions, {
                selections: userFilterSelections,
                search: userSearch,
                labels: userLabels,
                sortColumn: userSortColumn,
                sortDirection: userSortDirection,
            }),
        [
            userOptions,
            userFilterSelections,
            userSearch,
            userLabels,
            userSortColumn,
            userSortDirection,
        ],
    );
    const activeUserFilterCount = countActiveUserFilters(userFilterSelections);
    const editingUser = useMemo(
        () => userOptions.find((user) => user.id === editingUserId) || null,
        [userOptions, editingUserId],
    );

    const toggleUserFilterValue = (dimensionKey, value) => {
        setUserFilterSelections((currentSelections) => {
            const currentValues = currentSelections[dimensionKey] || [];
            const nextValues = currentValues.includes(value)
                ? currentValues.filter((entry) => entry !== value)
                : [...currentValues, value];
            const nextSelections = { ...currentSelections };

            // An empty array and a missing key mean the same thing to filterUsers, but
            // dropping the key keeps countActiveUserFilters and the Ryd button honest.
            if (nextValues.length > 0) {
                nextSelections[dimensionKey] = nextValues;
            } else {
                delete nextSelections[dimensionKey];
            }

            return nextSelections;
        });
    };

    const clearUserFilters = () => {
        setUserFilterSelections({});
        setUserSearch('');
    };

    const changeUserSort = (columnKey) => {
        if (columnKey === userSortColumn) {
            setUserSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setUserSortColumn(columnKey);
        setUserSortDirection('asc');
    };

    const openUserEditor = (user) => {
        if (!user || saving) return;
        if (loadingCompanies || loadingReceivers) {
            showMessage('Virksomheder og affaldsmodtagere hentes stadig.', 'warning');
            return;
        }

        setUserDraft({
            companies: [...user.companies],
            wasteReceivers: [...user.wasteReceivers],
            permissionsByCompanyId: { ...user.companyMemberships },
        });
        setEditingUserId(user.id);
    };

    const closeUserEditor = () => {
        if (saving) return;
        setEditingUserId('');
        setUserDraft(createEmptyUserDraft());
    };

    // company_memberships is keyed by company id, so it has to move with `companies`: a
    // company that is removed loses its permission entry, and one that is added gets an
    // all-false entry rather than none at all. Re-adding a company the saved user still has
    // restores the permissions it had, so a mis-click in the picker costs nothing.
    const updateUserDraftCompanies = (companyIds) => {
        const nextCompanies = normalizeIdList(companyIds);

        setUserDraft((currentDraft) => ({
            ...currentDraft,
            companies: nextCompanies,
            permissionsByCompanyId: nextCompanies.reduce((next, companyId) => {
                next[companyId] = normalizeCompanyMembershipPermissionEntry(
                    currentDraft.permissionsByCompanyId[companyId] ||
                        editingUser?.companyMemberships?.[companyId],
                );
                return next;
            }, {}),
        }));
    };

    const updateUserDraftWasteReceivers = (wasteReceiverIds) => {
        setUserDraft((currentDraft) => ({
            ...currentDraft,
            wasteReceivers: normalizeIdList(wasteReceiverIds),
        }));
    };

    const updateUserDraftMembershipPermission = (companyId, key, checked) => {
        setUserDraft((currentDraft) => ({
            ...currentDraft,
            permissionsByCompanyId: {
                ...currentDraft.permissionsByCompanyId,
                [companyId]: {
                    ...normalizeCompanyMembershipPermissionEntry(
                        currentDraft.permissionsByCompanyId[companyId],
                    ),
                    [key]: checked,
                },
            },
        }));
    };

    const getUserDraftMembershipPermission = (companyId) =>
        normalizeCompanyMembershipPermissionEntry(userDraft.permissionsByCompanyId[companyId]);

    const saveUserDraft = async () => {
        if (!editingUser) return;

        setSaving(true);

        try {
            const nextCompanies = normalizeIdList(userDraft.companies);
            const nextWasteReceivers = normalizeIdList(userDraft.wasteReceivers);
            const serializedMemberships = nextCompanies.reduce((next, companyId) => {
                next[companyId] = serializeCompanyMembershipPermissionEntry(
                    userDraft.permissionsByCompanyId[companyId],
                );
                return next;
            }, {});

            const userPayload = {
                companies: nextCompanies,
                waste_receivers: nextWasteReceivers,
                company_memberships:
                    Object.keys(serializedMemberships).length > 0
                        ? serializedMemberships
                        : deleteField(),
            };

            const batch = writeBatch(db);
            // `merge: true` would deep-merge company_memberships, so a company removed here
            // would keep its permission entry forever. `mergeFields` replaces each written
            // field wholesale, which is what makes a removal actually remove anything.
            batch.set(doc(db, 'users', editingUser.id), userPayload, {
                mergeFields: Object.keys(userPayload),
            });
            await batch.commit();

            setEditingUserId('');
            setUserDraft(createEmptyUserDraft());
            showMessage('Bruger gemt.');
        } catch (error) {
            console.error('Error saving user:', error);
            showMessage(`Kunne ikke gemme bruger: ${describeFirestoreError(error)}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    const reset = useCallback(() => {
        setEditingUserId('');
        setUserDraft(createEmptyUserDraft());
        setProvisioningDialog('');
        setWelcomeEmailUserId('');
    }, []);

    return {
        activeUserFilterCount,
        changeUserSort,
        clearUserFilters,
        closeUserEditor,
        editingUser,
        existingEmails,
        getShortCompanyLabel,
        getShortWasteReceiverLabel,
        getUserDraftMembershipPermission,
        openUserEditor,
        provisioningDialog,
        reset,
        setProvisioningDialog,
        setWelcomeEmailUserId,
        saveUserDraft,
        setUserSearch,
        toggleUserFilterValue,
        updateUserDraftCompanies,
        updateUserDraftMembershipPermission,
        updateUserDraftWasteReceivers,
        userDraft,
        userFilterGroups,
        userFilterSelections,
        userSearch,
        userSortColumn,
        userSortDirection,
        visibleUsers,
        welcomeEmailTemplates,
        welcomeEmailUser,
    };
};

export default useUsersTab;
export { createEmptyUserDraft };
